import React, { useState, useContext, useEffect } from 'react';
import styles from './ReferralPage.module.css';
import AuthContext from '../../context/AuthContext';
import posthog from 'posthog-js'; 

// Import logo directly - this ensures webpack processes it
import buddhaLogo from '../../assets/icons/buddha-logo.png';
// Import for meta tags
import { Helmet } from 'react-helmet';

// Components
import ReferralForm from './components/ReferralForm';
import ReferralShare from './components/ReferralShare';

const ReferralPage = () => {
    const authCtx = useContext(AuthContext);

    // State for the form fields
    const [referralEmail, setReferralEmail] = useState('');
    const [referralPhone, setReferralPhone] = useState('');
    const [userName, setUserName] = useState('');

    // Animation states
    const [animateForm, setAnimateForm] = useState(false);
    const [copied, setCopied] = useState(false);

    // State for submission status
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    // URL for sharing
    const shareUrl = window.location.origin + "/signup?ref=" + (authCtx.user?.username || "ecombuddha");
    
    // Create a correct absolute URL for the logo image
    // We need to create an absolute URL for the SVG content instead of relying on file paths
    const [actualLogoUrl, setActualLogoUrl] = useState('');
    // Identify user in PostHog when the component mounts
    useEffect(() => {
        if (authCtx.user && (authCtx.user.username || authCtx.user.sub)) {
            posthog.identify(
                authCtx.user.username || authCtx.user.sub,
                {
                    email: authCtx.user.email || '',
                    name: authCtx.user.name || '',
                    user_type: authCtx.user.user_type || ''
                }
            );
            
            // Track referral page view
            posthog.capture('referral_page_viewed');
        }
    }, [authCtx.user]);

    // Set up the logo URL
    useEffect(() => {
        const setupLogoUrl = async () => {
            try {
                // Try to fetch the SVG content directly
                const response = await fetch(buddhaLogo);
                const svgText = await response.text();
                
                // Create a blob with the SVG content
                const blob = new Blob([svgText], { type: 'image/svg+xml' });
                
                // Create a blob URL that can be used in meta tags
                const blobUrl = URL.createObjectURL(blob);
                setActualLogoUrl(blobUrl);
                
            } catch (error) {
                // Fallback to a direct path that's likely to work
                setActualLogoUrl(`${window.location.origin}/assets/icons/buddha.svg`);
            }
        };
        
        setupLogoUrl();
        
        // Clean up the blob URL when component unmounts
        return () => {
            if (actualLogoUrl && actualLogoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(actualLogoUrl);
            }
        };
    }, []);

    useEffect(() => {
        // Set animation after a brief delay
        setTimeout(() => {
            setAnimateForm(true);
        }, 300);

        // Set user name from context if available
        if (authCtx.user && authCtx.user.name) {
            setUserName(authCtx.user.name);
        }
    }, [authCtx.user]);


    const handleEmailChange = (event) => {
        setReferralEmail(event.target.value);
        setMessage('');
    };

    const handlePhoneChange = (event) => {
        setReferralPhone(event.target.value);
        setMessage('');
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
        
                // Track referral link copied event
                posthog.capture('referral_link_copied', {
                    method: 'clipboard',
                    share_url: shareUrl,
                });

            })
            .catch(err => {

                // Track copy error
                posthog.capture('referral_link_copy_error', {
                    error: err.message || 'Clipboard API error'
                });

            });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setMessage('');
        setIsError(false);

        if (!authCtx.token) {
            setMessage('You must be logged in to submit referrals.');
            setIsError(true);
            setIsLoading(false);

            // Track auth error
            posthog.capture('referral_submission_auth_error');

            return;
        }

                // Track referral attempt with contact info type
                const referralInfo = {
                    has_email: Boolean(referralEmail),
                    has_phone: Boolean(referralPhone),
                };
                
                posthog.capture('referral_attempt', referralInfo);


        try {
            const API_URL = process.env.REACT_APP_API_URL || '';
            const endpoint = `${API_URL}/auth/save_referral`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authCtx.token}`
                },
                body: JSON.stringify({
                    referred_email: referralEmail,
                    referred_phone: referralPhone
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `Failed to submit referral (Status: ${response.status}).`);
            }

            setMessage(data.message || 'Referral submitted successfully! Thank you.');
            setIsError(false);

            // Track successful referral
            posthog.capture('referral_submitted', {
                distinct_id: authCtx.user?.username || authCtx.user?.sub,
                contact_type: referralEmail && referralPhone ? 'both' : 
                            referralEmail ? 'email' : 'phone',
                submission_timestamp: new Date().toISOString(),
                // Include these properties directly in the event
                referral_count: 1, // Indicates this is one referral
                last_referral_date: new Date().toISOString(),
                last_referral_type: referralEmail && referralPhone ? 'both' : 
                                referralEmail ? 'email' : 'phone'
            });
            
            // Clear form and show thank you
            setTimeout(() => {
                setReferralEmail('');
                setReferralPhone('');
            }, 1000);

        } catch (error) {
            setMessage(error.message);
            setIsError(true);

            // Track submission error
            posthog.capture('referral_submission_error', {
                error_message: error.message || 'API error'
            });

        } finally {
            setIsLoading(false);
        }
    };

    const handleSocialShare = (platform) => {
        let shareLink = '';
        
        const shareText = `Check out EcomBuddha! It's an amazing tool for e-commerce. Sign up using my referral link: ${shareUrl}`;
        
        switch(platform) {
            case 'whatsapp':
                const uniqueTimestamp = Date.now();
                const whatsappUrl = `${shareUrl}?utm_source=whatsapp&t=${uniqueTimestamp}`;
                shareLink = `https://wa.me/?text=${encodeURIComponent(shareText + ` ${whatsappUrl}`)}`;
                
                // Track WhatsApp share
                posthog.capture('referral_shared', {
                    platform: 'whatsapp',
                    share_url: whatsappUrl,
                    share_action: 'completed',
                });

                window.open(shareLink, '_blank');
                break;
            case 'linkedin':
                shareLink = `https://www.linkedin.com/messaging/compose?body=${encodeURIComponent(shareText)}`;
                
                // Track LinkedIn share
                posthog.capture('referral_shared', {
                    platform: 'linkedin',
                    share_url: shareUrl,
                    share_action: 'completed',
                });

                window.open(shareLink, '_blank');
                break;
            case 'email':
                const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent('Join EcomBuddha - My Recommendation')}&body=${encodeURIComponent(`Heyy,\n\nI've been using EcomBuddha and thought you might find it useful too.\n\nHere’s my referral link if you want to check it out: ${shareUrl}\n\nBest,\n${userName || ''}`)}`;
                
                // Track Email share
                posthog.capture('referral_shared', {
                    platform: 'email',
                    share_url: shareUrl,
                    share_action: 'completed',
                });

                window.open(gmailLink, '_blank');
                break;
                
            default:
                return;
        }
        // Increment total shares count
        // posthog.people.increment('referral_shares_count');
    };
    

    // This function ensures meta tags are present for social sharing
    useEffect(() => {
        // Update or create meta tags for better social sharing
        const updateMetaTags = () => {
            // Only proceed if we have a valid logo URL
            if (!actualLogoUrl) {
                return;
            }
            
            // Meta tags for Open Graph (Facebook, LinkedIn)
            const metaTags = [
                { property: 'og:title', content: 'Join EcomBuddha - Transform Your E-commerce Business' },
                { property: 'og:description', content: 'EcomBuddha provides powerful tools to boost your e-commerce performance. Join now through my referral link!' },
                { property: 'og:image', content: actualLogoUrl },
                { property: 'og:url', content: shareUrl },
                { property: 'og:type', content: 'website' },
                // Twitter Card tags (important for Twitter shares)
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', content: 'Join EcomBuddha - Transform Your E-commerce Business' },
                { name: 'twitter:description', content: 'EcomBuddha provides powerful AI tools to boost your e-commerce performance. Join now through my referral link!' },
                { name: 'twitter:image', content: actualLogoUrl }
            ];
            
            // Update or create each meta tag
            metaTags.forEach(tagInfo => {
                let metaTag;
                if (tagInfo.property) {
                    metaTag = document.querySelector(`meta[property="${tagInfo.property}"]`);
                    if (!metaTag) {
                        metaTag = document.createElement('meta');
                        metaTag.setAttribute('property', tagInfo.property);
                        document.head.appendChild(metaTag);
                    }
                } else if (tagInfo.name) {
                    metaTag = document.querySelector(`meta[name="${tagInfo.name}"]`);
                    if (!metaTag) {
                        metaTag = document.createElement('meta');
                        metaTag.setAttribute('name', tagInfo.name);
                        document.head.appendChild(metaTag);
                    }
                }
                
                if (metaTag) {
                    metaTag.setAttribute('content', tagInfo.content);
                }
            });
            
            // Create a visible image element in the page that WhatsApp can detect
            // This increases the chances WhatsApp will use our image
            let visibleImg = document.getElementById('whatsapp-preview-image');
            if (!visibleImg) {
                // Create a container
                const container = document.createElement('div');
                container.style.position = 'absolute';
                container.style.opacity = '0.01'; // Almost invisible but not completely
                container.style.pointerEvents = 'none'; // Don't capture clicks
                container.style.bottom = '0';
                container.style.right = '0';
                container.style.zIndex = '-1'; // Behind everything
                document.body.appendChild(container);
                
                // Create the image
                visibleImg = document.createElement('img');
                visibleImg.id = 'whatsapp-preview-image';
                visibleImg.src = actualLogoUrl;
                visibleImg.alt = 'EcomBuddha Logo';
                visibleImg.width = 400;
                visibleImg.height = 400;
                container.appendChild(visibleImg);
                
            }
        };
        
        // Only run this effect if we have a logo URL
        if (actualLogoUrl) {
            updateMetaTags();
        }
        
    }, [shareUrl, actualLogoUrl]);

    return (
        <div className={styles.referralContainer}>
            {/* Add Helmet for better social media link previews */}
            <Helmet>
                <title>Join EcomBuddha - Transform Your E-commerce Business</title>
                <meta name="description" content="EcomBuddha provides powerful AI tools to boost your e-commerce performance. Join now through my referral link!" />
                <meta property="og:title" content="Join EcomBuddha - Transform Your E-commerce Business" />
                <meta property="og:description" content="EcomBuddha provides powerful AI tools to boost your e-commerce performance. Join now through my referral link!" />
                <meta property="og:image" content={actualLogoUrl} />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Join EcomBuddha - Transform Your E-commerce Business" />
                <meta name="twitter:description" content="EcomBuddha provides powerful AI tools to boost your e-commerce performance. Join now through my referral link!" />
                <meta name="twitter:image" content={actualLogoUrl} />
                {/* These additional meta tags help with WhatsApp previews */}
                <meta property="og:image:width" content="400" />
                <meta property="og:image:height" content="400" />
                <meta property="og:site_name" content="EcomBuddha" />
            </Helmet>
            
            <div className={styles.header}>
                <h1>Refer a Friend</h1>
                <p className={styles.description}>
                Share EcomBuddha with friends and colleagues who could benefit from our tool
                </p>
            </div>

            <div className={styles.referralCards}>
                <ReferralForm
                    referralEmail={referralEmail}
                    referralPhone={referralPhone}
                    isLoading={isLoading}
                    message={message}
                    isError={isError}
                    animateForm={animateForm}
                    handleSubmit={handleSubmit}
                    handleEmailChange={handleEmailChange}
                    handlePhoneChange={handlePhoneChange}
                />

                <ReferralShare
                    shareUrl={shareUrl}
                    copied={copied}
                    animateForm={animateForm}
                    copyToClipboard={copyToClipboard}
                    handleSocialShare={handleSocialShare}
                />
            </div>

        </div>
    );
};

export default ReferralPage;